import type { OrganizationPermissionName, PlatformPermissionName, EntitlementLevel } from '@/lib/db/types';

export const ORGANIZATION_PERMISSIONS = {
  ADMIN: 'org.admin' as const,
  MANAGE_MEMBERS: 'org.manage_members' as const,
  MANAGE_GROUP_ASSESSMENTS: 'org.manage_group_assessments' as const,
  MANAGE_DETAILS: 'org.manage_details' as const,
  MANAGE_BILLING: 'org.manage_billing' as const,
} as const;

export const PLATFORM_PERMISSIONS = {
  MANAGE_PRODUCT_A: 'platform.manage_productA' as const,
  MANAGE_PRODUCT_B: 'platform.manage_productB' as const,
} as const;

export const ENTITLEMENT_LEVELS = {
  NONE: 'none' as const,
  INDIVIDUAL: 'individual' as const,
  GROUP: 'group' as const,
} as const;

export const ORGANIZATION_PERMISSION_LABELS: Record<OrganizationPermissionName, string> = {
  'org.admin': 'Organisation Administrator',
  'org.manage_members': 'Manage Members',
  'org.manage_group_assessments': 'Manage Group Assessments',
  'org.manage_details': 'Manage Organisation Details',
  'org.manage_billing': 'Manage Billing',
};

export const ORGANIZATION_PERMISSION_DESCRIPTIONS: Record<OrganizationPermissionName, string> = {
  'org.admin': 'Full administrative access to the organization, including all settings and members',
  'org.manage_members': 'Add, edit, remove, and view organization members',
  'org.manage_group_assessments': 'Create, edit, and manage group assessments for the organization',
  'org.manage_details': 'Update organization name, details, and general settings',
  'org.manage_billing': 'Manage billing information, payment methods, and subscriptions',
};

export const PLATFORM_PERMISSION_LABELS: Record<PlatformPermissionName, string> = {
  'platform.manage_productA': 'Can Administer Conscious Brand Assessment',
  'platform.manage_productB': 'Can Administer Brand Archetype Assessment',
};

export const PLATFORM_PERMISSION_DESCRIPTIONS: Record<PlatformPermissionName, string> = {
  'platform.manage_productA': 'Access to Conscious Brand Assessment administration settings, including questions, recommendations, and assessments',
  'platform.manage_productB': 'Access to Brand Archetype Assessment administration settings and configuration',
};

export const ENTITLEMENT_LEVEL_LABELS: Record<EntitlementLevel, string> = {
  none: 'No Access',
  individual: 'Individual Assessments',
  group: 'Group Assessments',
};

export const ENTITLEMENT_LEVEL_DESCRIPTIONS: Record<EntitlementLevel, string> = {
  none: 'No access to this product',
  individual: 'Access to individual assessments only',
  group: 'Access to both individual and group assessments (includes all individual features)',
};

export const DEFAULT_OWNER_PERMISSIONS: OrganizationPermissionName[] = [
  ORGANIZATION_PERMISSIONS.ADMIN,
  ORGANIZATION_PERMISSIONS.MANAGE_MEMBERS,
  ORGANIZATION_PERMISSIONS.MANAGE_GROUP_ASSESSMENTS,
  ORGANIZATION_PERMISSIONS.MANAGE_DETAILS,
  ORGANIZATION_PERMISSIONS.MANAGE_BILLING,
];

export const ENTITLEMENT_HIERARCHY: Record<EntitlementLevel, number> = {
  none: 0,
  individual: 1,
  group: 2,
};

export function hasEntitlementLevel(
  userLevel: EntitlementLevel,
  requiredLevel: EntitlementLevel
): boolean {
  return ENTITLEMENT_HIERARCHY[userLevel] >= ENTITLEMENT_HIERARCHY[requiredLevel];
}

export const TESTING_MODE_ENABLED = import.meta.env.VITE_TESTING_MODE === 'true';
